import { FC, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Avatar,
    Typography,
    Grid,
    Card,
    CardActionArea,
    CardContent,
} from '@mui/material';
import { StoreActionType } from '../../../stores/redux/types';
import { useTranslation } from 'react-i18next';
import PublicIcon from '@mui/icons-material/Public';
import { useLocation } from 'react-router';

interface IProps {
    countryDatas?: any;
}

export const SCUIGeneralCountrySelectV2: FC<IProps> = ({ countryDatas }) => {
    const { t } = useTranslation();
    const setLastRegion = useSelector((state: any) => state.lastRegionReducer.setLastRegion);
    const dispatch: any = useDispatch();
    const [open, setOpen] = useState(false);

    const location = useLocation();

    const getCountryName = (country: any) => {
        if (country.translations && typeof country.translations === 'object') {
            if (country.translations[currentLang]) {
                return country.translations[currentLang];
            }
            const shortLang = currentLang.split('-')[0];
            if (country.translations[shortLang]) {
                return country.translations[shortLang];
            }
        }
        return country.countryName || country.countryCode;
    };

    const allCountries = useMemo(() => countryDatas || [], [countryDatas]);
    const sortedCountries = useMemo(() => {
        return allCountries.slice().sort((a: any, b: any) => {
            const nameA = getCountryName(a).toLowerCase();
            const nameB = getCountryName(b).toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [allCountries]);

    const handleCountrySelect = (countryId: any) => {
        dispatch({
            type: StoreActionType.SET_LAST_REGION,
            payload: {
                setLastRegion: countryId,
            },
        });
        const regionId = (countryDatas || []).find((x: any) => x.countryId === countryId)?.regionId?.toString();
        localStorage.setItem(
            'selectedCountry',
            JSON.stringify(countryDatas?.find((item: any) => item.countryId === countryId))
        );
        dispatch({
            type: StoreActionType.SET_COUNTRY,
            payload: {
                country: countryId,
            },
        });
        dispatch({
            type: StoreActionType.SET_REGION,
            payload: {
                region: regionId,
            },
        });
        setOpen(false);
        window.location.reload();
    };

    const selectedCountry = (countryDatas || []).find((item: any) => {
        if (!setLastRegion) {
            return countryDatas?.find((item: any) => item.isDefault);
        }
        return item.countryId === setLastRegion;
    });

    let currentLang = 'en-US';
    if (typeof window !== 'undefined') {
        const lang = localStorage.getItem('lan');
        if (typeof lang === 'string' && lang) {
            currentLang = lang;
        }
    }
    const getCountryNameWithEnglish = (country: any) => {
        const localName = getCountryName(country);
        const code = country.countryCode || '';
        if (code && code !== localName) {
            return `${localName} | ${code}`;
        }
        return localName;
    };

    return (
        <div className="sideBySideDiv">
            <Button
                variant="outlined"
                disabled={location?.pathname.startsWith('/campaign/campaignmanagement/save')}
                onClick={() => setOpen(true)}
                sx={{
                    marginLeft: 1,
                    minWidth: 120,
                    border: '1.5px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    background: '#fff',
                    padding: '6px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'border 0.2s, box-shadow 0.2s',
                    '&:hover': {
                        border: '1.5px solid #b3b3b3',
                        background: '#f7fafd',
                    },
                }}
            >
                {selectedCountry ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            alt={selectedCountry.code}
                            src={selectedCountry.flagUrl}
                            sx={{
                                width: 20,
                                height: 20,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                                border: '1px solid #e0e0e0',
                            }}
                        />
                        <span style={{ fontWeight: 400, fontSize: 15, color: '#1976d2', letterSpacing: 1 }}>
                            {selectedCountry.countryCode}
                        </span>
                    </span>
                ) : (
                    t('general.country')
                )}
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle
                    sx={{ textAlign: 'center', pb: 0, pt: 4, borderBottom: '1px solid #eee', background: '#fafbfc' }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                        <PublicIcon sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
                        <Typography
                            variant="h5"
                            align="center"
                            gutterBottom
                            sx={{ fontWeight: 700, fontSize: 28, letterSpacing: 0.5 }}
                        >
                            {t('general.chooseYourCountry')}
                        </Typography>
                    </div>
                </DialogTitle>
                <DialogContent sx={{ pt: 4, pb: 2, px: { xs: 1, sm: 4 }, minHeight: 320 }}>
                    <Grid
                        container
                        spacing={3}
                        justifyContent="center"
                        alignItems="stretch"
                        sx={{ pt: 6, overflow: 'visible' }}
                    >
                        {sortedCountries.map((option: any) => {
                            const isSelected = selectedCountry && selectedCountry.countryId === option.countryId;
                            return (
                                <Grid
                                    key={option.countryId}
                                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}
                                    size={{
                                        xs: 6,
                                        sm: 4,
                                        md: 3,
                                        lg: 2,
                                    }}
                                >
                                    <Card
                                        sx={{
                                            width: 120,
                                            minHeight: 110,
                                            borderRadius: 3,
                                            boxShadow: 2,
                                            transition: '0.2s',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            border: isSelected ? '2.5px solid #1976d2' : '1.5px solid #f0f0f0',
                                            background: isSelected
                                                ? 'linear-gradient(135deg, #e3f0fc 0%, #f6fbff 100%)'
                                                : '#fff',
                                            '&:hover': { boxShadow: 6, borderColor: '#1976d2', background: '#f0f7ff' },
                                            mb: 3,
                                        }}
                                    >
                                        <CardActionArea
                                            onClick={() => handleCountrySelect(option.countryId)}
                                            sx={{
                                                p: 1.5,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                height: '100%',
                                            }}
                                        >
                                            <Avatar
                                                alt={option.code}
                                                src={option.flagUrl}
                                                sx={{ width: 32, height: 32, mb: 1, boxShadow: 1 }}
                                            />
                                            <CardContent
                                                sx={{
                                                    p: 0,
                                                    textAlign: 'center',
                                                    flex: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minHeight: 24,
                                                    width: '100%',
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: 13,
                                                        color: '#222',
                                                        width: '100%',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {getCountryNameWithEnglish(option)}
                                                </Typography>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button onClick={() => setOpen(false)} sx={{ fontWeight: 600, fontSize: 16 }}>
                        {t('general.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
